import { CardComponentProps } from "@~/types/types";
import { useEffect, useRef, useState } from "react";
import {
  PrimaryButton as V31PrimaryButton,
  SecondaryButton as V31SecondaryButton,
} from "@JohnLewisPartnership/jl-design-system/dist/components/button/Button";
import {
  PrimaryButton as DeprecatedPrimaryButton,
  SecondaryButton as DeprecatedSecondaryButton,
} from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import {
  ATBEvent,
  triggerClickEvent,
} from "../__shared__/AddToBasketUtils/addToBasketUtils.ts";
import { EVENTS } from "../__shared__/AddToBasketUtils/basketEvents.ts";
import { useEmailConfirmed } from "@~/product-card-components/__shared__/AddToBasketUtils/hooks/useEmailConfirmed";
import { QuickViewModal } from "../__shared__/QuickViewModal/QuickViewModal";
import { useWindowSize } from "../../utils/hooks/useWindowSize";
import { useVariant } from "@~/contexts/VariantContext/VariantContext";
import {
  isAddToBasketProduct,
  isEmailWhenBackInStockProduct,
  isShipFromStoreProduct,
} from "../../utils/product";
import { GiftMessageModal } from "./GiftMessageModal/GiftMessageModal";
import { calculateUnfulfilledQuantity } from "./utils/calculateUnfulfilledQuantity";
import { dispatchAnalyticsEvent } from "@~/utils/analytics";
import { ADD_TO_BASKET, WISHLIST_ADD_TO_BASKET } from "@~/utils/events";
import styles from "./WishlistCallToAction.module.scss";
import { useCachedBasketData } from "@~/product-card-components/__shared__/AddToBasketUtils/hooks/useCachedBasket.ts";
import { useFeatureFlags } from "@~/flags/flags";
import { useProductCardEvent } from "@~/contexts/ProductCardEventContext/ProductCardEventContext.tsx";
import { productCardEvents } from "@~/contexts/ProductCardEventContext/productCardEvents";

type ATBState =
  | { status: "ACTIVE" }
  | { status: "ADDING" }
  | { status: "SUCCESS" }
  | { status: "ERROR"; error: string };

type WishlistView = "owner" | "shared";

export function WishlistCallToAction({
  product,
  configProps,
}: Pick<CardComponentProps, "product" | "configProps">) {
  const { variant } = useVariant();
  const { isJldsV31Enabled } = useFeatureFlags();
  const { width: windowWidth } = useWindowSize();
  const dispatchEvent = useProductCardEvent();

  const isSmallScreen = windowWidth ? windowWidth < 768 : false;

  const [atbState, setAtbState] = useState<ATBState>({ status: "ACTIVE" });
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const atbTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const clientId = (configProps?.clientId as string) ?? "wishlist";

  const wishlistView: WishlistView =
    configProps?.wishlistViewType === "owner" ? "owner" : "shared";

  const wanted = product.wanted ?? 1;
  const purchased = product.purchased ?? 0;
  const isFullyPurchased = purchased >= wanted;

  const shouldShowAddToBasket = isAddToBasketProduct(product);
  const shouldShowEmailWhenAvailable = isEmailWhenBackInStockProduct(product);
  const shouldShowMoreInformation =
    isShipFromStoreProduct(product) && !shouldShowAddToBasket;

  const hasHadEmailConfirmed = useEmailConfirmed(
    shouldShowEmailWhenAvailable,
    product?.aliases?.pimProductId,
  );

  const cachedBasketData = useCachedBasketData();
  const quantityInBasket =
    cachedBasketData?.find((bp) => bp.variantId === variant?.id)?.quantity ?? 0;

  useEffect(
    function subscribeToATBResponseEvents() {
      function onSuccess(e: Event) {
        if (abortControllerRef.current?.signal.aborted) return;
        const detail = (e as CustomEvent<ATBEvent>).detail;
        if (detail.variantId !== variant?.id) return;
        if (atbTimeoutRef.current) clearTimeout(atbTimeoutRef.current);
        setAtbState({ status: "SUCCESS" });
        dispatchAnalyticsEvent(ADD_TO_BASKET);
      }

      function onError(e: Event) {
        if (abortControllerRef.current?.signal.aborted) return;
        const detail = (e as CustomEvent<ATBEvent>).detail;
        if (detail.variantId !== variant?.id) return;
        if (atbTimeoutRef.current) clearTimeout(atbTimeoutRef.current);
        setAtbState({
          status: "ERROR",
          error: "Something went wrong, please try again later.",
        });
      }

      window.addEventListener(
        EVENTS.JL_EVENTS_ADD_TO_BASKET_SUCCESS,
        onSuccess,
      );
      window.addEventListener(EVENTS.JL_EVENTS_ADD_TO_BASKET_ERROR, onError);

      return () => {
        abortControllerRef.current?.abort();
        window.removeEventListener(
          EVENTS.JL_EVENTS_ADD_TO_BASKET_SUCCESS,
          onSuccess,
        );
        window.removeEventListener(
          EVENTS.JL_EVENTS_ADD_TO_BASKET_ERROR,
          onError,
        );
        if (atbTimeoutRef.current) clearTimeout(atbTimeoutRef.current);
      };
    },
    [variant?.id],
  );

  const handleAddToBasket = () => {
    if (!variant) return;

    const controller = new AbortController();
    abortControllerRef.current = controller;

    const quantity =
      wishlistView === "owner"
        ? calculateUnfulfilledQuantity({
            wanted: product.wanted,
            purchased: product.purchased,
            quantityInBasket,
          })
        : 1;

    triggerClickEvent({
      variantId: variant.id,
      skuId: product.aliases.pimProductId,
      quantity,
    });

    setAtbState({ status: "ADDING" });

    atbTimeoutRef.current = setTimeout(() => {
      if (controller.signal.aborted) return;
      setAtbState({
        status: "ERROR",
        error: "Something went wrong, please try again later.",
      });
    }, 8000);

    window.dispatchEvent(
      new CustomEvent(WISHLIST_ADD_TO_BASKET, {
        detail: { variantId: variant.id, quantity, clientId },
      }),
    );
  };

  const handleAtbClick = () => {
    dispatchEvent(productCardEvents.ctaAddToBasket);
    if (wishlistView === "shared") {
      setShowGiftModal(true);
    } else {
      handleAddToBasket();
    }
  };

  const handleGiftMessageConfirm = () => {
    setShowGiftModal(false);
    handleAddToBasket();
  };

  if (isFullyPurchased) {
    return (
      <div
        data-component="WishlistCallToAction"
        className={styles.fullyPurchased}
        data-testid="purchased-indicator"
      >
        Purchased
      </div>
    );
  }

  if (shouldShowAddToBasket) {
    return (
      <div data-component="WishlistCallToAction" ref={containerRef}>
        {isJldsV31Enabled?.value ? (
          <V31PrimaryButton
            className={styles.cta_button}
            onClick={handleAtbClick}
            data-testid="wishlist-add-to-basket"
            submitting={atbState.status === "ADDING"}
            disabled={atbState.status === "SUCCESS"}
            aria-label={
              atbState.status === "SUCCESS"
                ? "Added to basket"
                : "Add to basket"
            }
            size="small"
            labelText={
              atbState.status === "SUCCESS"
                ? "Added to basket"
                : "Add to basket"
            }
          />
        ) : (
          <DeprecatedPrimaryButton
            className={styles.cta_button}
            onClick={handleAtbClick}
            data-testid="wishlist-add-to-basket"
            submitting={atbState.status === "ADDING"}
            disabled={atbState.status === "SUCCESS"}
            aria-label={
              atbState.status === "SUCCESS"
                ? "Added to basket"
                : "Add to basket"
            }
            size="small"
          >
            {atbState.status === "SUCCESS"
              ? "Added to basket"
              : "Add to basket"}
          </DeprecatedPrimaryButton>
        )}
        {atbState.status === "ERROR" && (
          <p
            aria-live="assertive"
            className={styles.error}
            data-testid="wishlist-atb-error"
          >
            {atbState.error}
          </p>
        )}
        {wishlistView === "shared" && (
          <GiftMessageModal
            product={product}
            isOpen={showGiftModal}
            onClose={() => setShowGiftModal(false)}
            onConfirm={handleGiftMessageConfirm}
          />
        )}
      </div>
    );
  }

  if (shouldShowMoreInformation) {
    return (
      <div data-component="WishlistCallToAction">
        {isJldsV31Enabled?.value ? (
          <V31SecondaryButton
            className={styles.cta_button}
            href={variant?.pdpURL?.url}
            size="small"
            tag="a"
            data-testid="wishlist-more-information"
            labelText="More information"
            onClick={() => dispatchEvent(productCardEvents.ctaMoreInformation)}
          />
        ) : (
          <DeprecatedSecondaryButton
            className={styles.cta_button}
            href={variant?.pdpURL?.url}
            size="small"
            tag="a"
            data-testid="wishlist-more-information"
            onClick={() => dispatchEvent(productCardEvents.ctaMoreInformation)}
          >
            More information
          </DeprecatedSecondaryButton>
        )}
      </div>
    );
  }

  if (shouldShowEmailWhenAvailable) {
    if (!hasHadEmailConfirmed || showEmailModal) {
      return (
        <div data-component="WishlistCallToAction">
          {isJldsV31Enabled?.value ? (
            <V31SecondaryButton
              className={styles.cta_button}
              size="small"
              disabled={showEmailModal}
              submitting={showEmailModal}
              onClick={() => {
                dispatchEvent(productCardEvents.ctaEmailMe);
                setShowEmailModal(true);
              }}
              data-testid="wishlist-email-when-available"
              labelText="Email when available"
            />
          ) : (
            <DeprecatedSecondaryButton
              className={styles.cta_button}
              size="small"
              disabled={showEmailModal}
              submitting={showEmailModal}
              onClick={() => {
                dispatchEvent(productCardEvents.ctaEmailMe);
                setShowEmailModal(true);
              }}
              data-testid="wishlist-email-when-available"
            >
              Email when available
            </DeprecatedSecondaryButton>
          )}
          <QuickViewModal
            product={product}
            showModal={showEmailModal}
            onClose={() => setShowEmailModal(false)}
            config={isSmallScreen ? "quickAdd" : "standard"}
            originator={"ADD_TO_BASKET"}
          />
        </div>
      );
    }

    return (
      <div data-component="WishlistCallToAction">
        {isJldsV31Enabled?.value ? (
          <V31SecondaryButton
            className={styles.cta_button}
            href={variant?.pdpURL?.url}
            size="small"
            tag="a"
            data-testid="wishlist-email-confirmed"
            labelText="We'll be in touch"
            onClick={() => dispatchEvent(productCardEvents.ctaWellBeInTouch)}
          />
        ) : (
          <DeprecatedSecondaryButton
            className={styles.cta_button}
            href={variant?.pdpURL?.url}
            size="small"
            tag="a"
            data-testid="wishlist-email-confirmed"
            onClick={() => dispatchEvent(productCardEvents.ctaWellBeInTouch)}
          >
            We&apos;ll be in touch
          </DeprecatedSecondaryButton>
        )}
      </div>
    );
  }

  return null;
}
